import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";

import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import { InventoriesAutoComplete } from "@/features/ui/autoComplete/inventoryLot/inventory-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { useLoader } from "@/hooks/useLoader";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { Button, Group, Radio, Select, Table, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  AuthSignupPost201ResponseEquipmentWorksInner,
  ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner,
  ProductionActionApiWorksWorkIdInputPutRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  WorksItemGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Create = Object.assign({}, FormMain, {
  Button: Button,
  Input: TextInput,
  Select: Select,
  Date: DateInput,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  InventoriesAutoComplete: InventoriesAutoComplete,
});

type PageKeyType = "equipment" | "plan"; // pageKey의 가능한 값들을 정의합니다.

type ItemType<T extends PageKeyType> = T extends "equipment"
  ? AuthSignupPost201ResponseEquipmentWorksInner
  : ProductionPlansGet200ResponseRowsInnerWorksInner; // pageKey에 따른 item의 타입을 정의합니다.

interface WorkInputFormProps<T extends PageKeyType> {
  workRowData: ItemType<T>;
  workIds?: (number | undefined)[];
}

export function WorkInputForm<T extends PageKeyType>(params: WorkInputFormProps<T>): JSX.Element {
  const [itemCodes, setItemCodes] = useState<string | undefined>();
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const actions = useWorkActions();

  const { t } = useTranslation();
  const { setLoading, LoadingOverlay } = useLoader({ width: "100%", height: "100%" });

  const { mutate: workInputMutate } = useMutation(
    (params: ProductionActionApiWorksWorkIdInputPutRequest) =>
      mutateInventories
        .workInput(params)
        .mutationFn(params as ProductionActionApiWorksWorkIdInputPutRequest | any),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (res) => {
        queryClient.invalidateQueries(["inventories"]);
        queryClient.invalidateQueries(["workPage"]);
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === "workSlitting",
        });
        actions.setWorks((prevWorks) => []);
        actions.setScrapWorkIds(undefined);
        closeModal(form.values);
        setLoading(false);
        customAlert(
          t("workMessage.materialInputSuccess"),
          t("inventory.success", { item: t("work.materialInput") }),
          "green"
        );
      },
      onError: (error: any) => {
        setLoading(false);
        customAlert(
          handleErrorResponse({ error, errorContext: t("work.materialInput") }),
          t("inventory.fail", { item: t("work.materialInput") }),
          "red"
        );
      },
    }
  );

  const workData = params.workRowData;
  const workIds = params.workIds;

  const fromLocationCode = (workData as WorksItemGet200ResponseRowsInner)?.locationSetting
    ?.fromLocation?.code;
  const targetLocationCode = (workData as WorksItemGet200ResponseRowsInner)?.locationSetting
    ?.toLocation?.code;

  const form = useForm({
    initialValues: {
      workId: workData?.id,
      worksWorkIdInputPutRequest: {
        lotId: undefined,
        sourceLocationCode: fromLocationCode,
        // routingsData는 배열이므로, find로 해당 routingCode에 맞는 데이터를 찾아야 함
        targetLocationCode: targetLocationCode,
        quantity: undefined,
      },
    },
  });

  const bomItemCodes = workData?.routingData?.routingBoms?.map((value) => value.itemCode);

  const setInventoryIdAndSourceLocationCode = (
    data: ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner | undefined
  ) => {
    form.setFieldValue("worksWorkIdInputPutRequest.lotId", data?.lotId as any);
    form.setFieldValue(
      "worksWorkIdInputPutRequest.sourceLocationCode",
      data ? fromLocationCode : undefined
    );
    form.setFieldValue("worksWorkIdInputPutRequest.quantity", data?.quantity as any);
  };

  const onSubmit = () => {
    try {
      workInputMutate({
        workId: form.values.workId ?? 0,
        worksWorkIdInputPutRequest: {
          lotId: Number(form.values.worksWorkIdInputPutRequest.lotId),
          sourceLocationCode:
            form.values.worksWorkIdInputPutRequest.sourceLocationCode &&
            (workData as ProductionPlansGet200ResponseRowsInnerWorksInner)?.locationSetting
              ?.fromLocation?.code,
          targetLocationCode:
            form.values.worksWorkIdInputPutRequest.targetLocationCode &&
            (form.values.worksWorkIdInputPutRequest.targetLocationCode as string),
          quantity:
            form.values.worksWorkIdInputPutRequest.quantity &&
            (form.values.worksWorkIdInputPutRequest.quantity as string),
          groupWorkId:
            workIds !== undefined ? workIds?.filter((id): id is number => id !== undefined) : [],
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("work.materialInput") }),
        t("inventory.fail", { item: t("work.materialInput") }),
        "red"
      );
    }
  };

  return (
    <>
      <Create.Wrapper>
        <LoadingOverlay />
        <Table>
          <thead>
            <tr>
              <th>
                <span style={{ marginInline: "2rem 2rem", fontSize: "0.8rem" }}>
                  {t("item.code")}
                </span>
                <span style={{ fontSize: "0.8rem", marginInline: "2rem 4rem" }}>
                  {t("item.name")}
                </span>
                <span style={{ fontSize: "0.8rem" }}>{t("routing.code")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <Radio.Group
              value={itemCodes}
              onChange={(e) => {
                setItemCodes(e);
                form.setFieldValue("worksWorkIdInputPutRequest.lotId", undefined);
              }}
            >
              {(
                workData as ProductionPlansGet200ResponseRowsInnerWorksInner
              )?.routingData?.routingBoms?.map((value, index) => {
                return (
                  <tr key={index}>
                    <Group style={{ gap: "1rem" }}>
                      <Radio value={value.itemCode} />
                      <td style={{ wordBreak: "break-all", width: "9ch" }}>{value.itemCode}</td>
                      <td style={{ wordBreak: "break-all", width: "9ch" }}>{value?.item?.name}</td>
                      <td style={{ wordBreak: "break-all", width: "9ch" }}>{value.routingCode}</td>
                    </Group>
                  </tr>
                );
              })}
            </Radio.Group>
          </tbody>
        </Table>

        <Create.InventoriesAutoComplete
          key={form.values.worksWorkIdInputPutRequest.lotId}
          label={t("lot.lotInfo")}
          withAsterisk
          query={
            itemCodes
              ? {
                  itemCode: itemCodes,
                  locationCode: fromLocationCode,
                  quantity: { $gt: 0 },
                }
              : {
                  itemCode: bomItemCodes,
                  locationCode: fromLocationCode,
                  quantity: { $gt: 0 },
                }
          }
          {...form.getInputProps("worksWorkIdInputPutRequest.lotId")}
          setSelectedOption={(data) => {
            // setSelectedOption을 호출하여 선택된 옵션을 설정합니다.
            setInventoryIdAndSourceLocationCode(data);
          }}
        />
        <LocationAutoComplete
          label={t("user.fromLocationCode")}
          withAsterisk
          disabled
          value={fromLocationCode}
        />
        <LocationAutoComplete
          label={t("user.toLocationCode")}
          disabled
          {...form.getInputProps("worksWorkIdInputPutRequest.targetLocationCode")}
        />
        <Create.Input
          type="number"
          label={t("inventory.quantity")}
          {...form.getInputProps("worksWorkIdInputPutRequest.quantity")}
        />
        <Create.ButtonBox>
          <Create.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Create.Button>
          <Create.Button
            disabled={validateFormValues({
              pageType: "inputMaterial",
              values: form.values.worksWorkIdInputPutRequest,
            })}
            onClick={onSubmit}
          >
            {t("work.materialInput")}
          </Create.Button>
        </Create.ButtonBox>
      </Create.Wrapper>
    </>
  );
}
